// Secrets manager. Lists the names + updated-at of the user's secrets
// and lets them add a new one, replace an existing one, or delete.
//
// Values are write-only by design — there's no "show" button. Once
// set, a secret can be replaced (which overwrites) or deleted, but
// the existing value isn't retrievable through this UI (the server
// has `secrets.get` for agents, not for human eyes). The form's
// "Replace" mode just calls secrets.set with the new value.

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { confirm } from "../../components/confirm";
import { api, type SecretRef } from "../../lib/rpc";

interface SecretsModalProps {
  open: boolean;
  onClose: () => void;
  notify: (msg: string) => void;
}

// Server validation rule (mirrored from src/secrets.mjs isValidSecretName):
// must start with letter or underscore; only uppercase / digits / underscore
// permitted afterwards. Catching here gives a friendlier error than the
// raw RPC string.
const NAME_PATTERN = /^[A-Z_][A-Z0-9_]*$/;

export function SecretsModal({ open, onClose, notify }: SecretsModalProps) {
  const [secrets, setSecrets] = useState<SecretRef[] | null>(null);
  // Form state — either "adding a new secret" or "replacing an
  // existing one" (target name pre-filled, locked).
  const [editName, setEditName] = useState("");
  const [editValue, setEditValue] = useState("");
  const [editLocked, setEditLocked] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setEditName("");
    setEditValue("");
    setEditLocked(false);
    api.secretsList()
      .then((s) => { if (!cancelled) setSecrets(s); })
      .catch(() => { if (!cancelled) setSecrets([]); });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !busy) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, busy]);

  if (!open) return null;

  const refresh = async () => {
    try { setSecrets(await api.secretsList()); } catch {}
  };

  const submit = async () => {
    const name = editName.trim();
    const value = editValue;
    if (!name) { notify("Give the secret a name"); return; }
    if (!NAME_PATTERN.test(name)) {
      notify("Use UPPERCASE letters, digits, and underscores. Must start with a letter or underscore.");
      return;
    }
    if (!value) { notify("Value can't be empty"); return; }
    setBusy(true);
    try {
      await api.secretsSet(name, value);
      notify(editLocked ? "Secret replaced" : "Secret saved");
      setEditName("");
      setEditValue("");
      setEditLocked(false);
      await refresh();
    } catch (e: any) {
      notify(e?.message || "Save failed");
    } finally { setBusy(false); }
  };

  const startReplace = (name: string) => {
    setEditName(name);
    setEditValue("");
    setEditLocked(true);
  };

  const remove = async (name: string) => {
    const ok = await confirm({
      title: `Delete "${name}"?`,
      message: "Any agent or script reading this secret will fail until it's re-set.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.secretsDelete(name);
      setSecrets((prev) => (prev ? prev.filter((s) => s.name !== name) : prev));
      notify(`Deleted ${name}`);
    } catch (e: any) {
      notify(e?.message || "Delete failed");
    }
  };

  return (
    <div className="settings-submodal-bg" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="settings-submodal" onClick={(e) => e.stopPropagation()}>
        <header className="settings-submodal-head">
          <h3 className="settings-submodal-title">Secrets</h3>
          <button className="icon-btn" onClick={onClose} type="button">
            <Icon name="x" size={14} />
          </button>
        </header>

        <div className="settings-submodal-body">
          <p className="settings-paragraph">
            Stored encrypted on the server. Values are write-only here — your
            agent can read them, this UI can't. Replace a secret by clicking it
            and entering a new value.
          </p>

          <div className="settings-row">
            <label className="settings-row-label">
              {editLocked ? `Replace value for ${editName}` : "New secret"}
            </label>
            <input
              className="input"
              placeholder="NAME (uppercase, digits, underscores)"
              value={editName}
              disabled={editLocked || busy}
              onInput={(e) => setEditName((e.currentTarget as HTMLInputElement).value)}
            />
            <input
              className="input"
              type="password"
              placeholder="value"
              value={editValue}
              disabled={busy}
              onInput={(e) => setEditValue((e.currentTarget as HTMLInputElement).value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
            />
            <div className="settings-button-row">
              {editLocked && (
                <button
                  className="btn"
                  onClick={() => { setEditLocked(false); setEditName(""); setEditValue(""); }}
                  disabled={busy}
                  type="button"
                >
                  Cancel
                </button>
              )}
              <button
                className="btn btn-primary"
                onClick={submit}
                disabled={busy || !editName.trim() || !editValue}
                type="button"
              >
                <Icon name="check" size={13} />
                {busy ? "Saving…" : editLocked ? "Replace" : "Save secret"}
              </button>
            </div>
          </div>

          <div className="settings-row">
            <label className="settings-row-label">Existing ({secrets?.length ?? 0})</label>
            {secrets === null ? (
              <p className="settings-loading">Loading…</p>
            ) : secrets.length === 0 ? (
              <p className="settings-empty">No secrets yet.</p>
            ) : (
              <ul className="settings-list">
                {secrets.map((s) => (
                  <li key={s.name} className="settings-list-row">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="settings-list-row-title">
                        <code>{s.name}</code>
                      </div>
                      {s.updatedAt && (
                        <div className="settings-list-row-meta">
                          Updated {new Date(s.updatedAt).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <button className="btn" onClick={() => startReplace(s.name)} type="button">
                      <Icon name="edit" size={12} /> Replace
                    </button>
                    <button className="btn settings-danger-btn" onClick={() => remove(s.name)} type="button">
                      <Icon name="trash" size={12} /> Delete
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
